/**
 * The realised capital-gains statement, as Fintech Primitives reports it.
 *
 * Ported verbatim from the user platform's `helpers/mfCapitalGainsStatement.ts`.
 * It NEEDS NO NEW ENDPOINT: the rows arrive on `investor_reports.capital_gains`
 * inside the `reports_by_user_uuid` response the admin already fetches for the
 * dashboard, as a column list plus positional rows.
 *
 * It reports FP's own fields and does not compute tax. In particular it does
 * not classify STCG vs LTCG or apply a rate — `taxableGain`, `indexedCapitalGain`
 * and the grandfathering NAV are FP's figures, reproduced.
 */

export interface CapitalGainsReportTable {
  columns: string[];
  rows: (string | number | boolean | null)[][];
}

export interface CapitalGainStatementRow {
  folioNumber: string | null;
  isin: string | null;
  schemeName: string;
  transactionType: string | null;
  amount: number | null;
  units: number | null;
  tradedOn: string | null;
  tradedAt: number | null;
  daysHeld: number | null;
  purchasedOn: string | null;
  purchasedAt: number | null;
  actualGain: number | null;
  taxableGain: number | null;
  grandfathering: boolean | null;
  grandfatheringNav: number | null;
  indexedCostOfAcquisition: number | null;
  indexedCapitalGain: number | null;
}

export interface CapitalGainFinancialYear {
  key: string;
  label: string;
  startYear: number;
}

const asString = (value: unknown): string | null => {
  if (typeof value !== "string") return value == null ? null : String(value);
  const clean = value.trim();
  return clean || null;
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const asBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (/^(true|yes|1)$/i.test(value.trim())) return true;
    if (/^(false|no|0)$/i.test(value.trim())) return false;
  }
  return null;
};

const dateParts = (value?: string | null) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  return { year, month, day };
};

export const capitalGainFinancialYearForDate = (
  value?: string | null
): CapitalGainFinancialYear | null => {
  const parts = dateParts(value);
  if (!parts) return null;
  const startYear = parts.month >= 4 ? parts.year : parts.year - 1;
  return {
    key: String(startYear),
    label: `FY ${startYear}-${String(startYear + 1).slice(-2)}`,
    startYear,
  };
};

export const buildCapitalGainStatementRows = (
  table: CapitalGainsReportTable | null
): CapitalGainStatementRow[] => {
  if (!table) return [];
  const columnIndex = new Map(
    table.columns.map((column, index) => [column, index])
  );
  const value = (row: CapitalGainsReportTable["rows"][number], column: string) =>
    row[columnIndex.get(column) ?? -1];

  return table.rows.map((row) => ({
    folioNumber: asString(value(row, "folio_number")),
    isin: asString(value(row, "isin")),
    schemeName: asString(value(row, "scheme_name")) ?? "Scheme unavailable",
    transactionType: asString(value(row, "type")),
    amount: asNumber(value(row, "amount")),
    units: asNumber(value(row, "units")),
    tradedOn: asString(value(row, "traded_on")),
    tradedAt: asNumber(value(row, "traded_at")),
    daysHeld: asNumber(value(row, "source_days_held")),
    purchasedOn: asString(value(row, "source_purchased_on")),
    purchasedAt: asNumber(value(row, "source_purchased_at")),
    actualGain: asNumber(value(row, "source_actual_gain")),
    taxableGain: asNumber(value(row, "source_taxable_gain")),
    grandfathering: asBoolean(value(row, "grand_fathering")),
    grandfatheringNav: asNumber(value(row, "grand_fathering_nav")),
    indexedCostOfAcquisition: asNumber(
      value(row, "indexed_cost_of_acquisition")
    ),
    indexedCapitalGain: asNumber(value(row, "indexed_capital_gain")),
  }));
};

export const getCapitalGainFinancialYears = (
  rows: CapitalGainStatementRow[]
): CapitalGainFinancialYear[] => {
  const years = new Map<string, CapitalGainFinancialYear>();
  rows.forEach((row) => {
    const year = capitalGainFinancialYearForDate(row.tradedOn);
    if (year) years.set(year.key, year);
  });
  return Array.from(years.values()).sort((a, b) => b.startYear - a.startYear);
};

export const filterCapitalGainsByFinancialYear = (
  rows: CapitalGainStatementRow[],
  financialYearKey: string
): CapitalGainStatementRow[] =>
  rows
    .filter(
      (row) =>
        capitalGainFinancialYearForDate(row.tradedOn)?.key === financialYearKey
    )
    .sort((a, b) => (a.tradedOn ?? "").localeCompare(b.tradedOn ?? ""));
